import type { PayerResponseReason } from '../../../services/api/types.gen';

/**
 * Display strings for one payer reason (EMR-1241). These live outside the cell,
 * the list and the drawer because all three render the same reason and the
 * Billing Manager and the NOA Manager must not each carry a copy of the rule.
 *
 * The reason data itself comes from the shared read model (EMR-1240), which
 * already merged four evidence sources into one `source` field.
 */

const SOURCE_LABELS: Record<PayerResponseReason['source'], string> = {
  // Stedi refused the claim before the payer ever saw it.
  stedi: 'Clearinghouse',
  payer_277: 'Payer',
  payer_835: 'Payer',
};

export const sourceLabel = (source: string): string =>
  SOURCE_LABELS[source as PayerResponseReason['source']] ?? 'Unknown';

export interface FormattedReason {
  text: string;
  /** A muted second line, or null when the reason needs no apology. */
  hint: string | null;
}

/**
 * The unknown-code ladder: use the first rung the data supports, so the biller
 * always gets an actionable line rather than a blank cell.
 */
export const formatReason = (
  reason: Pick<PayerResponseReason, 'code' | 'description'>,
  payerClaimNumber?: string | null,
  kind: 'claim' | 'noa' = 'claim'
): FormattedReason => {
  const noun = kind === 'noa' ? 'NOA' : 'claim';
  const code = reason.code?.trim();
  const description = reason.description?.trim();

  if (code && description)
    return { text: `${code} — ${description}`, hint: null };
  if (description) return { text: description, hint: null };
  if (code) {
    return { text: code, hint: 'The payer did not include a description.' };
  }
  return {
    text: payerClaimNumber
      ? `The payer rejected this ${noun} but did not say why. Call the payer with claim # ${payerClaimNumber}.`
      : `The payer rejected this ${noun} but did not say why. Call the payer.`,
    hint: null,
  };
};
